import { FiExternalLink } from 'react-icons/fi';
import React, { useState, useEffect } from 'react';
import {
	StyledShareContentContainer,
	StyledShareConsentFormContainer,
	StyledShareTitle,
	StyledShareForm,
	StyledShareSpinnerContent,
	StyledShareSectionButton,
	StyledShareLoadingSpinner,
	StyledShareConsentButton,
	StyledShareErrorMessage,
	StyledShareImg,
} from 'components/Selfiebooth/ShareDisplay/styled'
import { TERMS_EN_URL, TERMS_FR_URL } from 'consts';
import { useTranslation } from 'react-i18next';
import { uploadOrUpdateFile } from 'store/actions/fileUpload'
import { dataURLtoFile } from 'utils/index'
import { useDispatch } from 'react-redux'
import { ThunkDispatch } from 'redux-thunk'
import { AnyAction } from 'redux'
import Controls from 'components/Selfiebooth/ShareDisplay/controls'
import FirebaseFileApi from 'libs/FirebaseApi/FileApi'
import { SelfieBoothType } from '../../../types/selfiebooth'
import { useOnlineStatus } from '../../../hooks/useOnlineStatus'

interface ConsentProps {
	selfiebooth: SelfieBoothType;
	app: any;
	finalImg?: string;
	visitorId: string;
	cardInfos: any;
	onCancel: () => void;
	nextStep: () => void;
	getFileId: (id: string) => void;
}

const Consent = ({ app, selfiebooth, finalImg, visitorId, onCancel, nextStep, cardInfos, getFileId }: ConsentProps) => {
	const [isChecked, setIsChecked] = useState<boolean>(false);
	const [isUploading, setIsUploading] = useState<boolean>(false);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const { part: infos } = selfiebooth;
	const { t, i18n } = useTranslation();
	const online = useOnlineStatus();
	const dispatch = useDispatch<ThunkDispatch<any, unknown, AnyAction>>();

	const termsUrl = infos.shareAdmin.language === 'en' ? TERMS_EN_URL : TERMS_FR_URL;

	useEffect(() => {
		i18n.changeLanguage(infos.shareAdmin.language);
	}, [infos.shareAdmin.language, i18n]);

	const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setIsChecked(event.target.checked);
	};

	const handleUploadFailure = (messageKey: string) => {
		if (infos.shareDownload?.show) {
			nextStep();
			return;
		}
		setUploadError(messageKey);
	};

	const handleNextStep = async () => {
		if (!isChecked) return;

		if (!online) {
			handleUploadFailure('pwaOffline.subtitle');
			return;
		}

		setIsUploading(true);
		setUploadError(null);

		try {
			const file = dataURLtoFile(finalImg, `photo-${Date.now()}.jpg`);

			const FUO = {
				appId: app.id,
				cardId: cardInfos.cardId,
				tokenId: cardInfos.tokenId,
				connectedInputs: {},
				part: { id: selfiebooth.id, type: infos.type },
				file: file,
				fileType: 'photo',
				credentials: app.credentials,
				source: "share-to-admin",
				tos: termsUrl,
			};

			const fileID = await dispatch(uploadOrUpdateFile(FUO, null, 'photo', null, visitorId, "selfiebooth"))

			const api = new FirebaseFileApi(app.id)
			await api.addFileToVoxpop(selfiebooth.id, fileID)
			await api.off()

			getFileId(fileID);
			nextStep();
		} catch (error) {
			console.error("Erreur lors de l'upload de l'image :", error);
			handleUploadFailure('photobooth.error');
		} finally {
			setIsUploading(false);
		}
	};

	return <>
		<StyledShareContentContainer>
			<Controls source="consent" onCancel={onCancel} />
			<StyledShareImg src={finalImg} />
			<StyledShareConsentFormContainer>
				<StyledShareTitle>{infos.shareAdmin.title}</StyledShareTitle>
				<StyledShareForm>
					<label htmlFor="consent">
						<input
							type="checkbox"
							id="consent"
							checked={isChecked}
							onChange={handleCheckboxChange}
						/>
						{t('photobooth.termsConditions')}
					</label>
					<a href={termsUrl} target="_blank" data-ga="system link send-to-admin ms">
						<FiExternalLink />
					</a>
					<StyledShareSectionButton>
						{isUploading ? (
							<StyledShareSpinnerContent>
								<StyledShareLoadingSpinner />
							</StyledShareSpinnerContent>
						) : (
							<StyledShareConsentButton onClick={handleNextStep} disabled={!isChecked || isUploading}>
								<label>{infos.shareAdmin.button || t('photobooth.continue')}</label>
							</StyledShareConsentButton>
						)}
					</StyledShareSectionButton>
					{uploadError && (
						<StyledShareErrorMessage role="alert">{t(uploadError)}</StyledShareErrorMessage>
					)}
				</StyledShareForm>
			</StyledShareConsentFormContainer>
		</StyledShareContentContainer>
	</>;
};

export default Consent;
